<template>
    <div
        class="col-span-12 lg:col-span-6 p-6 md:min-h-[400px] bg-white dark:bg-slate-800 shadow dark:shadow-slate-900/50 overflow-hidden rounded-lg"
    >
        <div
            class="flex justify-between items-center"
            v-if="homeAirports.length > 0 || dreamDestinations.length > 0"
        >
            <div class="flex items-center gap-2">
                <icon icon="travel-goals" class="text-slate-800 dark:text-slate-100" />
                <span class="text-lg font-medium text-slate-800 dark:text-slate-100">
                    Travel goals
                </span>
            </div>
            <Link
                href="/user-settings/travel-goals"
                class="link-base link-dark"
            >
                Change
                <icon icon="arrow" />
            </Link>
        </div>
        <div
            class="w-full flex flex-col items-center justify-center gap-2 lg:gap-4 py-6"
            v-if="homeAirports.length === 0 && dreamDestinations.length === 0"
        >
            <img
                src="../../../../images/travel-goals-promo.png"
                alt="Add destination"
            />
            <div class="flex flex-col gap-2">
                <p class="text-3xl font-bold text-center">
                    <span class="text-slate-800 dark:text-slate-100">
                        Set Your
                        <br />
                        <span class="text-orange">Travel Goals</span>
                    </span>
                </p>
                <p
                    class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5 text-center"
                >
                    Get personalized recommendations, benefits, and offer
                    reminders.
                </p>
            </div>
            <Link href="/user-settings/travel-goals" class="btn btn-orange">
                Add travel goals
            </Link>
        </div>
        <div v-else class="w-full relative pb-6">
            <div class="flex items-start gap-4">
                <Icon icon="flight-departure" class="text-slate-500 mt-0.5" />
                <div>
                        <p class="font-medium text-sm text-slate-500 dark:text-slate-400">
                        Home Airport{{ homeAirports.length > 1 ? 's' : '' }}
                    </p>
                    <ul
                        v-if="homeAirports.length > 0"
                        class="mb-7"
                        :class="{
                            'list-disc pl-4 airports-divider':
                                homeAirports.length > 1,
                        }"
                    >
                        <li
                            v-for="airport in homeAirports"
                            :key="airport.code"
                                class="text-sm font-medium text-slate-800 dark:text-slate-100"
                        >
                            {{ airport.name }}, {{ airport.city }},
                            {{ airport.country }}
                        </li>
                    </ul>
                        <p v-else class="text-sm text-slate-500 dark:text-slate-400 mb-7">
                        No home airport, add one
                    </p>
                </div>
            </div>
            <hr
                v-if="homeAirports.length === 1"
                class="w-10 rotate-90 border-t border-slate-300 dark:border-slate-700 absolute top-12 -left-3"
            />

            <div class="flex items-start gap-4">
                <Icon icon="flight-arrival" class="text-slate-500 mt-0.5" />
                <div class="w-full">
                        <p class="font-medium text-sm text-slate-700 dark:text-slate-300 mb-1">
                        My dream destinations
                    </p>
                    <ul
                        class="flex w-full pt-3 max-md:overflow-x-scroll max-md:snap-x max-md:snap-mandatory max-md:scroll-smooth"
                        v-if="dreamDestinations.length > 0"
                    >
                        <li
                            class="w-[80%] min-w-[80%] md:min-w-0 md:w-1/3 bg-white dark:bg-slate-700 md:shadow dark:md:shadow-slate-900/50 rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.06),0px_1px_3px_0px_rgba(0,0,0,0.10)] overflow-hidden p-2 max-md:snap-always max-md:snap-start"
                            v-for="(destination, dIndex) in dreamDestinations"
                            :key="destination.id"
                            :class="{
                                'md:-rotate-3': dIndex === 0,
                                'md:rotate-3 md:-ml-2': dIndex === 1,
                                'md:-rotate-2 md:-ml-2': dIndex === 2,
                            }"
                        >
                            <image-loader v-if="destination.image_url">
                                <template #image>
                                    <img
                                        loading="lazy"
                                        :src="destination.image_url"
                                        :alt="destination.destination"
                                        width="400"
                                        height="300"
                                        class="w-full aspect-[4/3] rounded-md mb-2 object-cover"
                                    />
                                </template>
                                <template #preloader>
                                    <div
                                        class="w-full aspect-[4/3] rounded-md mb-2 bg-slate-200 dark:bg-slate-700 shimmer"
                                    ></div>
                                </template>
                                <template #error>
                                    <div
                                        class="w-full aspect-[4/3] rounded-md mb-2 bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-400"
                                    >
                                        <icon icon="no-image" />
                                    </div>
                                </template>
                            </image-loader>
                            <div
                                v-else
                                class="w-full aspect-[4/3] rounded-md mb-2 bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-400"
                            >
                                <icon icon="no-image" />
                            </div>
                                <p class="font-medium text-md text-slate-700 dark:text-slate-300">
                                {{ destination.destination }}
                            </p>
                        </li>
                    </ul>
                    <div v-else>
                            <p class="text-sm text-slate-500 dark:text-slate-400 mb-3">
                            No destinations, add yours
                        </p>
                        <img
                            src="../../../../images/travel-goals-promo.png"
                            alt="Add destination"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { Link } from '@inertiajs/vue3';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import { Apii } from '@/services/api';
import { ref, watch } from 'vue';
import { useDashboardStore } from '@/store/dashboard';
import { storeToRefs } from 'pinia';

const ApiService = new Apii();
const homeAirports = ref([]);
const dreamDestinations = ref([]);
const dashboardStore = useDashboardStore();
const { user } = storeToRefs(dashboardStore);

function fetchTravelGoals(targetUser) {
    const params = targetUser?.uuid ? `?uuid=${targetUser.uuid}` : '';

    ApiService.apiCall(`/user-settings/travel-goals${params}`, 'get')
        .then((response) => {
            homeAirports.value = response.data.home_airports || [];
            dreamDestinations.value = response.data.destinations;
        })
        .catch(() => {});
}

fetchTravelGoals(user.value);

watch(user, (newUser) => {
    fetchTravelGoals(newUser);
});
</script>

<style scoped>
.airports-divider {
    position: relative;
}

.airports-divider::after {
    content: '';
    position: absolute;
    left: -1.75rem;
    top: 5px;
    width: 1px;
    height: 100%;
    background-color: #d1d5db;
}
</style>
